import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AuthSevices,
  StaffNotificationService,
  ToastMessageService,
} from '@fitness-force/api';
import { NotificationLevel, NotificationPreference } from '@fitness-force/enum';
import {
  PatchStaffNotificationTopics,
  StaffNotification,
  StaffNotificationTopics,
  TENANTSINTERFACE,
} from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from 'apps/ff-frontend/src/app/state/app.state';
import { environment } from 'apps/ff-frontend/src/environments/environment';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DrawerModule } from 'primeng/drawer';
import * as PusherPushNotifications from '@pusher/push-notifications-web';
import { SkeletonModule } from 'primeng/skeleton';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import Statsig from 'statsig-js';
// import Statsig from 'statsig-js';

@Component({
  selector: 'staff-notification',
  standalone: true,
  imports: [
    CommonModule,
    DrawerModule,
    ToggleSwitchModule,
    FormsModule,
    ReactiveFormsModule,
    SkeletonModule,
    TranslateModule,
    ButtonModule,
    SelectModule,
    MessageModule,
  ],
  templateUrl: './staff-notification.component.html',
})
export class StaffNotificationComponent {
  notificationsForm: FormGroup = {} as FormGroup;
  staffSubscribedNotification: StaffNotification = {} as StaffNotification;
  allNotifications: { key: string; label: string }[] = [];
  tenantAccess: any[] = [];
  tenantId: number = 0;
  companyId: number = 0;
  initializeNotificationSdk: boolean = false;
  showSkeleton: boolean = true;
  NotificationLevelEnum = Object.entries(NotificationLevel).filter((key) => typeof key[1] != 'number').map(([key, value]) => ({ key, value }));
  currentUserDetails: any;
  notificationsBlocked: boolean = false;
  hasSubscribedTopics: boolean = false;

  constructor(
    private staffNotificationService: StaffNotificationService,
    private fb: FormBuilder,
    public cdr: ChangeDetectorRef,
    private store: Store,
    private toastMessageService: ToastMessageService,
    public translate: TranslateService,
    public authService:AuthSevices
  ) {}

  ngOnInit() {
    this.currentUserDetails = this.authService.currentUserDetails();
    this.showSkeleton = true;
    this.notificationsForm = this.fb.group({});
    this.NotificationLevelEnum = this.NotificationLevelEnum.map((o) => { return { key: o.key, value: o.value, disabled:false }; });
    //this.validateClaims();
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantAccess = tenant.accessed_tenant;
      this.tenantId = tenant.selected_tenant.id;
      this.companyId = tenant.selected_tenant.company_id;
    });
    this.getNotifications();
    this.checkNotificationPermission();
    this.cdr.detectChanges();
  }

  async checkNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.permission;
      this.notificationsBlocked = permission === 'denied';
      this.cdr.detectChanges();
    }
  }

  getNotifications() {
    this.staffNotificationService.getNotifications().subscribe({
      next: (res) => {
        this.staffSubscribedNotification = res;

        this.staffSubscribedNotification.subscribed_topics = this.staffSubscribedNotification.subscribed_topics.filter(topic => topic.notificationSubscriptionTopic !== 'SignupCompletionTargetDevice');
        if (this.staffSubscribedNotification.subscribed_topics.length === 0) {
          this.initializeNotificationSdk = true;
        }
        this.hasSubscribedTopics = this.staffSubscribedNotification.subscribed_topics.some(topic => topic.isSubscribed);
        this.allNotifications = this.getEnumValues(NotificationPreference);
        this.createFormControls();
        this.showSkeleton = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.createFormControls();
        this.showSkeleton = false;
        this.toastMessageService.showError(err);
        this.cdr.detectChanges();
      },
    });
  }

  getNotificationLevelOptions(topic:string)
  {
    const topics :any =['IncomingSMS','IncomingEmail','IncomingWhatsApp'];
    if(this.currentUserDetails && this.currentUserDetails.Id && topics.includes(topic))
    {
      if (this.currentUserDetails.role === 'Admin') {
       return this.NotificationLevelEnum.map((o) => {
          return { key: o.key, value: o.value, disabled: false };
        });
      } else if (this.currentUserDetails.role === 'Staff') {
          const canAdminMemberCommunication = this.currentUserDetails['can_admin_membercommunication'] == 'true';
          if(canAdminMemberCommunication){
            return this.NotificationLevelEnum.map((o) => {
              return { key: o.key, value: o.value, disabled: false };
            });
          }
          else{
            return this.NotificationLevelEnum.map((o) => {
              if (['All', 'Department'].includes(o.value.toString())) {
                 return { key: o.key, value: o.value, disabled: true };
              }
              return { key: o.key, value: o.value, disabled: false };
            });
          }
      }
      else 
        return this.NotificationLevelEnum.map((o) => { return { key: o.key, value: o.value, disabled: false}; });
    }
    else{
      return this.NotificationLevelEnum.map((o) => { return { key: o.key, value: o.value, disabled: false}; });
    }
  }

  createFormControls(): void {
    var excludeTopics:any[]= ['IncomingSMS','IncomingEmail','IncomingWhatsApp'];
    var isTwoWayNotificationEnabled = Statsig.checkGate("show_staff_2way_message_notifciation");
    var isDesktopNotificatuonEnabled = Statsig.checkGate("enable_staff_desktop_notification");
    const formControls =
      this.staffSubscribedNotification.subscribed_topics.reduce(
        (controls: any, notification) => {
          var level = notification.notificationSubscriptionTopic+"_level";
          const isSubscribed = this.staffSubscribedNotification
            .subscribed_topics
            ? this.staffSubscribedNotification.subscribed_topics.find((o) => {
                return o.notificationSubscriptionTopic === notification.notificationSubscriptionTopic;
              })?.isSubscribed
            : false;
          const control = new FormControl(isSubscribed, []);
          var notificationLevel = this.NotificationLevelEnum.find((o) => {
            return (o.value.toString() === notification.notificationLevel.toString());
          });
          const notificationLevelControl = new FormControl(notificationLevel, [Validators.required]);
          controls[notification.notificationSubscriptionTopic] = control;
          controls[level] = notificationLevelControl;

          if(excludeTopics.includes(notification.notificationSubscriptionTopic)){
            controls[level].enable();
          }
          else{
            controls[level].setValue(this.NotificationLevelEnum.find((o) =>  { return (o.value == 'All'); }));
            controls[level].disable();
            controls[level].updateValueAndValidity();
          }

           if(!isTwoWayNotificationEnabled && excludeTopics.includes(notification.notificationSubscriptionTopic))
           {
              controls[notification.notificationSubscriptionTopic].disable();
              controls[notification.notificationSubscriptionTopic].updateValueAndValidity();
           }
           else if(!isDesktopNotificatuonEnabled && !isTwoWayNotificationEnabled)
           {
              controls[level].disable();
              controls[level].updateValueAndValidity();
              controls[notification.notificationSubscriptionTopic].disable();
              controls[notification.notificationSubscriptionTopic].updateValueAndValidity();
           }
           else if(!isDesktopNotificatuonEnabled && isTwoWayNotificationEnabled)
           {
              if(excludeTopics.includes(notification.notificationSubscriptionTopic)){
                controls[level].enable();
                controls[notification.notificationSubscriptionTopic].enable();
                controls[notification.notificationSubscriptionTopic].updateValueAndValidity();
              }
              else{
                controls[level].disable();
                controls[notification.notificationSubscriptionTopic].disable();
                controls[level].updateValueAndValidity();
                controls[notification.notificationSubscriptionTopic].updateValueAndValidity();
              }
           }
          return controls;
        },
        {}
      );
    this.notificationsForm = this.fb.group(formControls);
  }

  getEnumValues(enumObj: any): { key: string; label: string }[] {
    return Object.keys(enumObj)
      .filter((key) => isNaN(Number(key))) // Filter out numeric keys
      .map((key) => ({ key, label: this.formatLabel(key) }));
  }

  formatLabel(key: string): string {
    return key.replace(/([A-Z])/g, ' $1').trim();
  }

  UpdateValue(key: string, event: any) {
    this.onNotificationChange(key, event.checked);
  }

  UpdateLevel(key: string, event: any) {
    if(this.notificationsForm.get(key)?.value)
       this.onNotificationChange(key, this.notificationsForm.get(key)?.value);
  }

  onNotificationChange(key: string, value: boolean): void {
    if(this.notificationsForm.get(key+'_level')?.value && this.notificationsForm.get(key+'_level')?.value.value){
      let request: Array<{ op: string; path: string; value: any }> = [];
      var payload: PatchStaffNotificationTopics[] = [] as PatchStaffNotificationTopics[];
      payload.push({ NotificationSubscriptionTopic: key, IsSubscribed: value, NotificationLevel: this.notificationsForm.get(key+'_level')?.value.value });
      request.push({
        op: 'replace',
        path: '/CreateUpdateStaffSubscribedNotificationRequest',
        value: payload,
      });
      this.staffNotificationService.patchNotifications(request).subscribe({
        next: (response: any) => {
          if (value) {
            this.translate
              .get('STAFF.STAFF_PREFERENCE.TOPIC_SUBSCRIBED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
          } else {
            this.translate
              .get('STAFF.STAFF_PREFERENCE.TOPIC_UNSUBSCRIBED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
          }
          this.staffSubscribedNotification.subscribed_topics.forEach(
            (notification) => {
              if (notification.notificationSubscriptionTopic === key) {
                notification.isSubscribed = value;
              }
            }
          );
          this.store.dispatch({
            type: 'FETCH_STAFF_NOTIFICATIONS',
            staffNotifications: this.staffSubscribedNotification,
            companyId: this.companyId,
          });
          if (this.initializeNotificationSdk) {
            this.ConfigurePusher();
          }
        },
        error: (error) => {
          this.toastMessageService.showError(error);
        },
      });
    }
    else{
      this.notificationsForm.get(key+'_level')?.setErrors({required: true});
      this.notificationsForm.get(key+'_level')?.markAsTouched();
      this.notificationsForm.get(key)?.setValue(false);
    }
    
  }

  ConfigurePusher() {
    const beamsClient = new PusherPushNotifications.Client({
      instanceId: environment.PUSHER_INSTANCE_ID,
    });
    beamsClient.start().then(() => console.log('Registered with beams!'));
    beamsClient
      .start()
      .then((beamsClient: any) => beamsClient.getDeviceId())
      .then((deviceId) =>
        console.log('Successfully registered with Beams. Device ID:', deviceId)
      )
      .then(() => {
        this.staffSubscribedNotification.tenant_ids.forEach((t) => {
          this.staffSubscribedNotification.subscribed_topics.forEach(
            (topic) => {
              if (topic.isSubscribed)
                beamsClient.addDeviceInterest(
                  `${this.companyId}_${t}_${topic.notificationSubscriptionTopic}`
                );
            }
          );
        });
      })
      .then(() => beamsClient.getDeviceInterests())
      // .then((interests) => console.log("Current interests:", interests))
      .catch(console.error);
  }

  disableAll() {
    if (
      this.staffSubscribedNotification.subscribed_topics.filter(
        (o) => o.isSubscribed
      ).length > 0
    ) {
      let request: Array<{ op: string; path: string; value: any }> = [];
      var payload: StaffNotificationTopics[] = [] as StaffNotificationTopics[];
      this.staffSubscribedNotification.subscribed_topics.forEach(
        (notification) => {
          this.notificationsForm
            .get(notification.notificationSubscriptionTopic)
            ?.setValue(false);
          payload.push({
            notificationSubscriptionTopic: notification.notificationSubscriptionTopic,
            isSubscribed: false,
            notificationLevel: notification.notificationLevel,
          });
        }
      );
      request.push({
        op: 'replace',
        path: '/PushNotificationInterests',
        value: payload,
      });
      this.staffNotificationService.patchNotifications(request).subscribe({
        next: (response: any) => {
          this.translate
            .get('STAFF.STAFF_PREFERENCE.DISABLE_ALL_SUCCESS')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
        },
        error: (error) => {
          this.toastMessageService.showError(error);
        },
      });
    }
  }

  public get  showDisableAll() {
    return this.staffSubscribedNotification.subscribed_topics && this.staffSubscribedNotification.subscribed_topics.filter(
      (o) => o.isSubscribed
    ).length > 0;
  }

  getFormControl(key: string): FormControl {
    return this.notificationsForm.get(key) as FormControl;
  }

  
}

